import * as React from "react";

import Bugsnag from "@bugsnag/js";
import {
  ActionIcon,
  ActionIconProps,
  Alert,
  Button,
  Center,
  CopyButton,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconClipboard, IconClipboardCheck, IconShare2 } from "@tabler/icons-react";
import objectHash from "object-hash";

import { useBackendClient } from "../../hooks/useBackendClient";
import { useCountEvent } from "../../hooks/useCountEvent";
import { useCurrentEditorDocument } from "../../hooks/useEditorState";
import { serializedChecklistDocument } from "../../lib/state/convert";
import { store } from "../../lib/state/store";

export const ShareButton = ({
  actionIconProps,
  iconSize,
}: {
  actionIconProps: ActionIconProps;
  iconSize?: string | number | undefined;
}): JSX.Element => {
  const [shareModalOpen, { open: openShareModal, close: closeShareModal }] = useDisclosure(false);
  const [shareId, setShareId] = React.useState<string | null>(null);
  const [shareError, setShareError] = React.useState<string | null>(null);

  const [abortController, setAbortController] = React.useState(new AbortController());

  const currentDocumentHash = useCurrentEditorDocument((state) => objectHash(state), "");
  React.useEffect(() => {
    setShareId(null);
  }, [currentDocumentHash, setShareId]);

  const countEvent = useCountEvent();

  const { POST } = useBackendClient();
  const shareDocument = React.useCallback(async () => {
    setShareError(null);
    openShareModal();

    const editingIndex = store.getState().library.editingEntry;
    if (editingIndex !== null && !shareId) {
      const currentDocument = store.getState().library.entries[editingIndex].present;
      const serializedDocument = serializedChecklistDocument(currentDocument);

      countEvent("share-document", currentDocument.title);

      POST("/share", {
        body: serializedDocument,
        signal: abortController.signal,
      })
        .then(({ data, error }) => {
          if (error) {
            if (error.message) {
              // TODO: report error to bugsnag
              setShareError(error.message);
            } else {
              // TODO: report error to bugsnag
              setShareError(
                "An error occurred when attempting to share the document. Please try again."
              );
            }
          } else if (!data.documentId) {
            // TODO: report error to bugsnag
            setShareError("There was a problem sharing the document. Please try again.");
          } else {
            // TODO: report error to bugsnag
            setShareId(data.documentId);
          }
        })
        .catch((error) => {
          // TODO: report error to bugsnag
          if (error === "user-cancelled") {
            return;
          }
          Bugsnag.notify(error);
          setShareError(
            "An error occurred when attempting to share the document. Please try again."
          );
        });
    }
  }, [setShareId, shareId, setShareError, abortController]);

  const closeAndCancelRequest = React.useCallback(() => {
    closeShareModal();
    abortController.abort("user-cancelled");
    setAbortController(new AbortController());
  }, [closeShareModal, abortController]);

  return (
    <>
      <ShareResultModal
        open={shareModalOpen}
        onClose={closeAndCancelRequest}
        shareId={shareId}
        errorMessage={shareError}
      />
      <Tooltip label="Share Document" openDelay={500} withArrow>
        <ActionIcon color="cyan" {...actionIconProps} onClick={() => shareDocument()}>
          <IconShare2 size={iconSize} />
        </ActionIcon>
      </Tooltip>
    </>
  );
};

const ShareResultModal = ({
  open,
  onClose,
  shareId,
  errorMessage,
}: {
  open: boolean;
  onClose: () => void;
  shareId: string | null;
  errorMessage: string | null;
}): JSX.Element => {
  const loading = shareId === null && errorMessage === null;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      centered
      size="lg"
      title={<Title order={3}>Share Document</Title>}
    >
      <Stack justify="space-between">
        {loading ? (
          <Center>
            <Loader size="md" />
          </Center>
        ) : (
          <>
            {errorMessage || !shareId ? (
              <Alert color="orange" radius="md" variant="light" title="Error">
                <Text>{errorMessage}</Text>
              </Alert>
            ) : (
              <Stack spacing="xs">
                <Text>This link below can be used by anyone to access your document:</Text>
                <TextInput
                  size="md"
                  value={buildLink(shareId)}
                  readOnly
                  styles={{
                    input: {
                      cursor: "pointer",
                    },
                  }}
                  onClick={(e) => e.currentTarget.select()}
                  rightSection={
                    <CopyButton value={buildLink(shareId)} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip label="Copy URL" withArrow>
                          <ActionIcon
                            onClick={copy}
                            color={copied ? "green" : "cyan.5"}
                            variant="filled"
                          >
                            {copied ? (
                              <IconClipboardCheck size={18} />
                            ) : (
                              <IconClipboard size={18} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  }
                />
                <Text fz="sm">
                  This URL is permanent and unique for the current state of your document. If you
                  change the document, you'll need to generate a new share URL.
                </Text>
              </Stack>
            )}
          </>
        )}
        <Group position="right">
          <Button size="sm" onClick={() => onClose()}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const buildLink = (id: string): string => {
  const currentDomain = window.location.host;
  const scheme = window.location.protocol;
  return `${scheme}//${currentDomain}/shared/${id}`;
};
